<script>
    import { Chart, registerables } from 'chart.js';
    import { createEventDispatcher } from 'svelte';
    import { Tooltip } from 'sveltestrap';
    import Spinner from 'svelte-spinner';

    import { formatDate, getApiDateFormat, isToday, getIsoDateStringAsDate } from '../../date-time.js';
    import { vptServer, token, reportDate, selectedAspect, inmateCount } from '../../stores.js';
    import { config } from "../../config";
    import { createRequestParams } from "../../string.js";

    const dispatch = createEventDispatcher();
    
    export let requestProperties;

    let chart;
    Chart.register(...registerables);

    let overviewData;
    let preventAutorender = true;

    let reportDateDescription;
    $: { if ($reportDate != undefined) { 
            reportDateDescription = "on " + formatDate(getApiDateFormat($reportDate));
        }
    }

    $: {
        if (requestProperties && requestProperties.requestDate) {
            dispatch('dataLoad', { isLoading: true } );
            getOverallOverview(requestProperties.requestDate + createRequestParams(requestProperties.requestParams));
        }
    }

    async function getOverallOverview(requestString) {
        if (!preventAutorender) {
            try {
                chart.destroy();
                overviewData = null;
            }
            catch {
            }
            preventAutorender = true;
        }

        await fetch(`${$vptServer}/establishment/overview/${requestString}`, {
            headers: {
                "Authorization": "Bearer " + $token
            }
        })
            .then(r => {
                if (!r.ok) {
                    throw Error(r.status);
                }
                return r;
            })
            .then(r => r.json())
            .then(data => {
                overviewData = data;
                dispatch('dataLoad', { isLoading: false } );
                inmateCount.set({
                    filtered: data.countTotalInmates, unfiltered: data.countTotalUnfilteredInmates
                });
            })
            .catch(error => {
                console.error("Error getting establishment overall overview");
                console.error(error);
                return;
            });

            renderOverallChart();

            // Allow filter changes to update the graph
            preventAutorender = false;
    }

    function renderOverallChart() {
        if (overviewData && overviewData.countTotalInmates > 0) {
            var ctx = document.getElementById("overall-chart-canvas").getContext("2d");
            chart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    labels: ['Targeted', 'Not Targeted'],
                    datasets: [{
                        label: 'Overall Summary',
                        data: [ {count: overviewData.countTargetedInmates, percentage: overviewData.percentageTargetedInmates}, 
                                {count: (overviewData.countTotalInmates - overviewData.countTargetedInmates), percentage: (100 - overviewData.percentageTargetedInmates)}
                            ],
                        backgroundColor: ['#880E4F', '#d4d5d6'],
                    }]
                },
                options: {
                    cutout: '65%',
                    maintainAspectRatio: false,
                    parsing: { 
                        key: 'count'
                    },
                    plugins: {
                        legend: {display: false},
                        tooltip: {
                            displayColors: false,
                            callbacks: {
                                label: function(context) {
                                    return context.label + ": " + context.raw.percentage + "%";
                                }
                            }
                        }
                    }
                }
            });
        }
    }
</script>

<style>
    .establishment-widget {
        width: 300px;
        margin-left: 15px;
    }

    #m-aspect-target-0 {
        padding-left: 5px;
    }
</style>

<div class="establishment-widget">
    {#if overviewData && overviewData.countTotalInmates > 0 && !($inmateCount.filtered > $inmateCount.unfiltered)}
        <button on:click class="button-reset focus-outline card" class:selected={$selectedAspect === "Overall"}>
            <div class="card-body">
                <div class="widget-container">
                    <span class="widget-label">Overall</span>

                    <div class="widget-content widget-pad-right">
                        <div class="chart-widget">
                            <canvas id="overall-chart-canvas"></canvas>

                            <span id="count-indicator-overall" class="chart-count"
                                    class:trend-up={overviewData.trendIndicatorTargetedInmates == 'U'}
                                    class:trend-down={overviewData.trendIndicatorTargetedInmates == 'D'}>
                                {overviewData.countTargetedInmates}
                            </span>
                            <Tooltip target="count-indicator-overall" placement="bottom">
                                {overviewData.countTargetedInmates} of {overviewData.countTotalInmates} ({overviewData.percentageTargetedInmates}%) {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'}
                                targeted on at least 1 aspect {isToday($reportDate) ? 'today' : reportDateDescription}
                            </Tooltip>

                            {#if overviewData.trendIndicatorTargetedInmates == 'U'}
                                <span id="trend-indicator-overall-u" class="trend-indicator trend-up">
                                    <img src="/assets/arrow/arrow-up-danger.svg" alt="Increasing Trend"/>
                                </span>
                                <Tooltip target="trend-indicator-overall-u" placement="bottom">
                                    Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted on at least 1 aspect
                                </Tooltip>
                            {:else if overviewData.trendIndicatorTargetedInmates == 'D'}
                                <span id="trend-indicator-overall-d" class="trend-indicator trend-down">
                                    <img src="/assets/arrow/arrow-down-success.svg" alt="Decreasing Trend"/>
                                </span>
                                <Tooltip target="trend-indicator-overall-d" placement="bottom">
                                    Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedInmates}% were targeted on at least 1 aspect
                                </Tooltip>
                            {/if}
                        </div>

                        <div class="breakdown-widget">
                                <table class="table table-sm table-borderless">
                                    <tbody>
                                        {#if $config.surveyUrn}
                                            <tr>
                                                <td id="m-aspect-target-over">
                                                    <img src="/assets/target-indicator-danger.svg" alt="Target" /><img src="/assets/target-indicator-danger.svg" alt="Target" /><img src="/assets/plus-circle-danger.svg" alt="Target" />
                                                </td>
                                                <td id="legend-overall-count-over">
                                                    {overviewData.countTargetedOver2}
                                                    <Tooltip target="legend-overall-count-over"
                                                                placement="bottom">{overviewData.countTargetedOver2}
                                                        of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'} targeted on over 2 aspects
                                                        {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                                </td>
                                                <td class="card-legend-percentage" id="legend-overall-percentage-2">
                                                    ({overviewData.percentageTargetedOver2}%)
                                                </td>
                                                <td class="card-legend-trend" id="legend-overall-trend-over">
                                                    {#if overviewData.trendIndicatorTargetedOver2 == 'U'}
                                                        <span class="trend-icon text-danger"><i class="bi bi-arrow-up-short" /></span>
                                                        <Tooltip target="legend-overall-trend-over"
                                                                 placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedOver2}% were targeted on over 2 aspects
                                                        </Tooltip>
                                                    {:else if overviewData.trendIndicatorTargetedOver2 == 'D'}
                                                        <span class="trend-icon text-success"><i class="bi bi-arrow-down-short" /></span>
                                                        <Tooltip target="legend-overall-trend-over"
                                                                 placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargetedOver2}% were targeted on over 2 aspects
                                                        </Tooltip>
                                                    {:else}
                                                        <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                                    {/if}
                                                </td>
                                            </tr>
                                        {/if}
                                        <tr>
                                            <td id="m-aspect-target-2"><img src="/assets/target-indicator-danger.svg" alt="Target" /><img src="/assets/target-indicator-danger.svg" alt="Target" /></td>
                                            <td id="legend-overall-count-2">
                                                {overviewData.countTargeted2}
                                                <Tooltip target="legend-overall-count-2"
                                                            placement="bottom">{overviewData.countTargeted2}
                                                    of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'} targeted on 2 aspects
                                                    {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                            </td>
                                            <td class="card-legend-percentage" id="legend-overall-percentage-2">
                                                ({overviewData.percentageTargeted2}%)
                                            </td>
                                            <td class="card-legend-trend" id="legend-overall-trend-2">
                                                {#if overviewData.trendIndicatorTargeted2 == 'U'}
                                                    <span class="trend-icon text-danger"><i class="bi bi-arrow-up-short" /></span>
                                                    <Tooltip target="legend-overall-trend-2"
                                                             placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted2}% were targeted on 2 aspects
                                                    </Tooltip>
                                                {:else if overviewData.trendIndicatorTargeted2 == 'D'}
                                                    <span class="trend-icon text-success"><i class="bi bi-arrow-down-short" /></span>
                                                    <Tooltip target="legend-overall-trend-2"
                                                             placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted2}% were targeted on 2 aspects
                                                    </Tooltip>
                                                {:else}
                                                    <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                                {/if}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td id="m-aspect-target-1"><img src="/assets/target-indicator.svg" alt="Target" /></td>
                                            <td id="legend-overall-count-1">
                                                {overviewData.countTargeted1}
                                                <Tooltip target="legend-overall-count-1"
                                                            placement="bottom">{overviewData.countTargeted1}
                                                    of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'} targeted on 1 aspect
                                                    {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                            </td>
                                            <td class="card-legend-percentage" id="legend-overall-percentage-1">
                                                ({overviewData.percentageTargeted1}%)
                                            </td>
                                            <td class="card-legend-trend" id="legend-overall-trend-1">
                                                {#if overviewData.trendIndicatorTargeted1 == 'U'}
                                                    <span class="trend-icon"><i class="bi bi-arrow-up-short" /></span>
                                                    <Tooltip target="legend-overall-trend-1"
                                                             placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted1}% were targeted on 1 aspect
                                                    </Tooltip>
                                                {:else if overviewData.trendIndicatorTargeted1 == 'D'}
                                                    <span class="trend-icon"><i class="bi bi-arrow-down-short" /></span>
                                                    <Tooltip target="legend-overall-trend-1"
                                                             placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted1}% were targeted on 1 aspect
                                                    </Tooltip>
                                                {:else}
                                                    <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                                {/if}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td id="m-aspect-target-0">-</td>
                                            <td id="legend-overall-count-0">
                                                {overviewData.countTargeted0}
                                                <Tooltip target="legend-overall-count-0"
                                                            placement="bottom">{overviewData.countTargeted0}
                                                    of {overviewData.countTotalInmates} {$config.inmateLabelName.toLowerCase()}s {isToday($reportDate) ? 'are' : 'were'} not targeted on any aspects
                                                    {isToday($reportDate) ? 'today' : reportDateDescription}</Tooltip>
                                            </td>
                                            <td class="card-legend-percentage" id="legend-overall-percentage-0">
                                                ({overviewData.percentageTargeted0}%)
                                            </td>
                                            <td class="card-legend-trend" id="legend-overall-trend-0">
                                                {#if overviewData.trendIndicatorTargeted0 == 'U'}
                                                    <span class="text-success trend-icon"><i class="bi bi-arrow-up-short" /></span>
                                                    <Tooltip target="legend-overall-trend-0"
                                                             placement="bottom">Increasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted0}% were not targeted on any aspects
                                                    </Tooltip>
                                                {:else if overviewData.trendIndicatorTargeted0 == 'D'}
                                                    <span class="text-danger trend-icon"><i class="bi bi-arrow-down-short" /></span>
                                                    <Tooltip target="legend-overall-trend-0"
                                                             placement="bottom">Decreasing Trend - Over the {isToday($reportDate) ? 'last' : 'previous'} {$config.trendPastDaysToAnalyse} days an average of {overviewData.trendPercentageTargeted0}% were not targeted on any aspects
                                                    </Tooltip>
                                                {:else}
                                                    <span class="text-disabled trend-icon"><i class="bi bi-dash" /></span>
                                                {/if}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                        </div>
                    </div>
                </div>
            </div>
        </button>
    {:else if overviewData}
        <div class="card" class:selected={$selectedAspect === "Overall"}>
            <div class="card-body">
                <div class="widget-container">
                    <span class="widget-label">Overall</span>

                    <div class="widget-content">
                        {#if ($inmateCount.filtered > $inmateCount.unfiltered) || ($inmateCount.unfiltered == 0 && isToday(getIsoDateStringAsDate(requestProperties.requestDate)))}
                            <span class="no-records">Data is being regenerated.</span>
                        {:else}
                            <span class="no-records">No AIM data is available for {isToday($reportDate) ? 'today' : formatDate(getApiDateFormat($reportDate))}.</span>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    {:else}
        <div class="card" class:selected={$selectedAspect === "Overall"}>
            <div class="card-body">
                <div class="widget-container">
                    <div class="widget-spinner">
                        <Spinner size="100" speed="750" color="#3d5980" thickness="3" gap="15" />
                    </div>
                </div>
            </div>
        </div>
    {/if}
</div>
